/**
 * The opening paragraph an engine row's explanation needs where the deposit is paid from money the
 * plan already has coming — a retirement village's exit payment, or the refund of a current room's
 * deposit — rather than from anything sold (owner-caught, 9 Oct 2026: "Explain this" on
 * "Pay 100% RAD ($750,000, all from the village money)" opened empty, because nothing was sold and
 * the explanation treated that as "nothing decided"). Built exactly as the advice document builds
 * it from the same row, so the two say the same thing.
 */
import { villagePiece } from './villageExitWords.ts';
import { movePiece } from './facilityMoveWords.ts';
import { phaseFromLabel } from './carePhases.ts';
import { describeFundingKeys, fundingPhrase } from './fundingLabels.ts';
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

type Row = {
    radPaidNet?: unknown;
    radRoomPrice?: unknown;
    radFundingManualKeys?: Record<string, number>;
    strategyOptions?: { villageExit?: { person?: string }; facilityMove?: { person?: string } } | Record<string, unknown>;
    strategySpecificAgedCareCosts?: { dailyAccommodationPayment?: number } | Record<string, unknown>;
    villageExit?: { leaveDate?: string; refundDate?: string; entitlement?: number; fromVillage?: number; fromSavings?: number; toBank?: number; interim?: { days: number; perDay: number; total: number } | null; feesAfterLeaving?: unknown; paidAtMove?: boolean } | null;
    facilityMove?: { stayPut?: boolean; moveDate?: string; newRoomPrice?: number; fromRefund?: number; fromSale?: number; grandfathered?: boolean } | null;
};

const monthsBetween = (from?: string, to?: string) =>
    from && to ? Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / (30.44 * 24 * 60 * 60 * 1000))) : 0;

const firstNameOf = (factFind: DraftFactFind, person: unknown) =>
    (person === 'partner' ? factFind.partner?.firstName : factFind.client?.firstName) || 'the client';

export function engineRowPieces(row: Row | null | undefined, factFind: DraftFactFind): { villageExit: string | null; facilityMove: string | null } {
    if (!row) return { villageExit: null, facilityMove: null };
    const opts = (row.strategyOptions || {}) as { villageExit?: { person?: string }; facilityMove?: { person?: string } };
    const dapPerDay = Number((row.strategySpecificAgedCareCosts as { dailyAccommodationPayment?: number } | undefined)?.dailyAccommodationPayment) || 0;
    const soldPhrase = fundingPhrase(describeFundingKeys(factFind as never, row.radFundingManualKeys), formatCurrency);
    const ve = row.villageExit;
    const villageExit = ve && ve.refundDate
        ? villagePiece({
              name: firstNameOf(factFind, opts.villageExit?.person),
              leaveLabel: ve.leaveDate ? phaseFromLabel(ve.leaveDate) : 'the coming weeks',
              entitlement: Number(ve.entitlement) || 0,
              refundMonths: monthsBetween(ve.leaveDate, ve.refundDate),
              refundDate: ve.refundDate,
              roomPrice: Number(row.radRoomPrice) || 0,
              deposit: Number(row.radPaidNet) || 0,
              fromVillage: Number(ve.fromVillage) || 0,
              fromSavings: Number(ve.fromSavings) || 0,
              soldPhrase,
              toBank: Number(ve.toBank) || 0,
              interim: ve.interim ?? null,
              feesAfterLeaving: (ve.feesAfterLeaving ?? null) as never,
              paidAtMove: !!ve.paidAtMove,
              dapPerDay,
          })
        : null;
    const fm = row.facilityMove;
    const person = opts.facilityMove?.person;
    const stay = (person === 'partner' ? factFind.partner : factFind.client) as { careStay?: { facilityName?: string } } | undefined;
    const facilityName = String(stay?.careStay?.facilityName || '');
    const facilityMove = fm
        ? fm.stayPut
            ? `${firstNameOf(factFind, person)} stays at ${facilityName || 'the current home'}: the deposit held stays where it is and nothing is sold.`
            : movePiece({
                  name: firstNameOf(factFind, person),
                  facilityName,
                  moveLabel: fm.moveDate ? phaseFromLabel(fm.moveDate) : 'the coming weeks',
                  newRoomPrice: Number(fm.newRoomPrice) || 0,
                  deposit: (Number(fm.fromRefund) || 0) + (Number(fm.fromSale) || 0),
                  fromRefund: Number(fm.fromRefund) || 0,
                  fromSale: Number(fm.fromSale) || 0,
                  soldPhrase,
                  dapPerDay,
                  grandfathered: !!fm.grandfathered,
                  retentionDaysTaken: 0,
              })
        : null;
    return { villageExit, facilityMove };
}
